/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild, OnDestroy, ChangeDetectorRef,  ElementRef, HostListener} from '@angular/core';
import { ActivatedRoute, RouterStateSnapshot } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  FormulaBuilderComponent,
  FormulaPropagationService,
  highlightAll,
  whenFormInvalid,
} from '@gh/builder-shared';
import { isFormulaEmpty, isFormulaValid } from '@gh/builder-shared-data';
import {
  ActionFlow,
  AppRouter,
  compoundLogicalAction,
  eitherAction,
  exitAction,
  FormState,
  getRouteInitData,
  LogicalAction,
  State,
  streamOptionalValueAction,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode, getControlValue, NotificationService, setControlValue, UploadContext } from '@gh/core-ui';
import { wrapInObservable } from '@gh/core-util';
import { from$ } from '@gh/rx';
import { filter$, mapTo$, debounceTime$ } from '@gh/rx/operators';
import { assign, find, get } from 'lodash';
import { Observable, Subscription } from 'rxjs';
import {
  ComponentDetails,
  needsFormulaPropagation,
  UomSharedListItem,
  whenItemsInvalid,
  whenNoItems,
} from '../../resources/component-and-item-details.model';
import { ComponentDetailsBusinessService } from '../../resources/component-details-business.service';
import { ComponentDetailsCommandService } from '../component-details-items-tab/component-details-command.service';
import {
  COMPONENT_DETAILS_ITEMS_INIT_STATE,
  ComponentDetailsItemsState,
  componentDetailsReducer,
} from '../component-details-items-tab/component-details-items.state';
import { ComponentOrItemFormState } from '../component-or-item-form-state';
import { ComponentDetailsFormState } from './component-details-form.service';
import { GridComponent } from '@gh/core-ui-grid';
import { InputMatchingService } from '../../../shared/services/input-matching.service';

enum ComponentDetailsTab { Items = 'ITEMS', Formula = 'FORMULA', Picture = 'PICTURE', Memo = 'MEMO' }

export function componentDetailsItemsStateFactory(): State<ComponentDetailsItemsState> {
  return new State(COMPONENT_DETAILS_ITEMS_INIT_STATE, componentDetailsReducer);
}

@Component({
  selector: 'gh-item-details-tab-container',
  template: require('./component-details-tab-container.component.html'),
  styles: [require('./component-details-tab-container.component.scss'),require('../../../details-tab-container.scss')],
  providers: [
    ComponentDetailsFormState,
    { provide: ComponentOrItemFormState, useExisting: ComponentDetailsFormState },
    UploadContext,
    { provide: State, useFactory: componentDetailsItemsStateFactory },
    ComponentDetailsCommandService,
  ],
})
export class ComponentDetailsTabContainerComponent extends AbstractFormComponent<ComponentDetails> implements OnInit, OnDestroy {
  uoms: UomSharedListItem[];

  selectedTab = ComponentDetailsTab.Items;
  matchingInput: string[] = [];
  private inputSubscription: Subscription;
  private ignoreInputValueChanges = false;
    
  @ViewChild('inputFieldWrapper', { static: true }) inputFieldWrapper: ElementRef;
  @ViewChild(FormulaBuilderComponent, { static: true }) formulaBuilder: FormulaBuilderComponent;
  @ViewChild('codeField') fieldToFocus!: ElementRef;

  constructor(private route: ActivatedRoute,
              public form: ComponentDetailsFormState,
              private itemsState: State<ComponentDetailsItemsState>,
              private navigationService: BuilderNavigationService,
              private businessService: ComponentDetailsBusinessService,
              private formulaPropagation: FormulaPropagationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private notificationService: NotificationService,
              private commands: ComponentDetailsCommandService,
              private router: AppRouter,
              private inputMatchingService: InputMatchingService,
              private cdr: ChangeDetectorRef
            ) {
    super();
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_component_create',
      [FormMode.EditMode]: 'lbl_component_edit',
      [FormMode.ViewMode]: 'lbl_component_view',
    }[this.form.mode];
  }

  get isFormulaNotPropagatable(): boolean {
    const formula = getControlValue(this.form.group, 'formula');
    return isFormulaEmpty(formula) || !isFormulaValid(formula);
  }

  onFormulaPropagate(): void {
    if (this.validateFormula()) {
      this.propagateFormula();
    }
  }

  get completeFlow(): ActionFlow<FormFlowState<ComponentDetails>> {
    return this.pipelines.complete({
      validateForm: this.validateFormAction,
      validate: this.validateAction,
      execute: this.saveAndPropagateAction,
      finish: (state: FormFlowState<ComponentDetails>) => {
        const { value, form } = state;
        this.ignoreInputValueChanges = true;
        if (form.createMode) {
          this.router.switchTo(['/setup/estimating/components', value.id]);
        } else {
          form.build(value);
          setTimeout(() => this.ignoreInputValueChanges = false, 1000);
        }
      },
    });
  }

  get saveAndPropagateAction(): LogicalAction<FormFlowState<ComponentDetails>> {
    const saveAction = this.commonActions.execute((state: FormFlowState<ComponentDetails>) =>
      this.businessService.save(state.value));
    return compoundLogicalAction('execute.success', 'execute.fail', {
      'start': eitherAction(({ value }: FormFlowState<ComponentDetails>) =>
        value.id ? (needsFormulaPropagation(value)) : false).bindAs('save-and-propagate', 'save'),
      'save': saveAction.bindAs('success', 'fail'),
      'save-and-propagate': saveAction.bindAs('propagate-formula', 'fail'),
      'propagate-formula': streamOptionalValueAction((state) =>
        this.propagateFormula().pipe(mapTo$(state))).bindAs('success', 'fail'),
      'success': exitAction('execute.success'),
      '*': exitAction('execute.fail'),
    });
  }

  get recalculationNeeded(): boolean {
    return this.itemsState.value.isRecalculationNeeded;
  }

  get validateFormAction(): LogicalAction<FormFlowState<ComponentDetails>> {
    return this.commonActions.validateWithEffect(whenFormInvalid, (state) => this.handleErrors(state));
  }

  get validateAction(): LogicalAction<FormFlowState<ComponentDetails>> {
    return this.commonActions.validateWithEffect(
      (state: FormFlowState<ComponentDetails>) => whenFormInvalid(state) || whenItemsInvalid(state.value),
      (state: FormFlowState<ComponentDetails>) => this.showError(state),
    );
  }

  ngOnInit(): void {
    const { component, ...init } = getRouteInitData(this.route);

    this.form.build(component);
    this.setupInputWatcher();

    assign(this, init);
  }

  ngAfterViewInit(): void {
    if(this.form.mode === 1)this.fieldToFocus.nativeElement.focus()
  }

  private setupInputWatcher(): void {
    const codeControl = this.form.group.get('code');
    if (codeControl) {
      this.inputSubscription = this.inputMatchingService.setupInputWatcher(
        codeControl.valueChanges,
        () => this.ignoreInputValueChanges,
        searchString => this.inputMatchingService.loadMatchingInput(
          searchString,
          this.businessService.getMatchingComponentCodes.bind(this.businessService),
          (codes: string[]) => (this.matchingInput = codes),
          this.cdr
        )
      );
    }
  }
  
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.inputMatchingService.clearMatchingInputOnClick(
      event,
      this.inputFieldWrapper.nativeElement,
      () => (this.matchingInput = []),
      this.cdr
    );
  }
  
  trackInput(index: number, code: string): string {
     return code;
  }
  
  ngOnDestroy(): void {
    this.inputMatchingService.unsubscribe(this.inputSubscription);
  }

  canDeactivate(nextState?: RouterStateSnapshot): Observable<boolean> | boolean {
    // Avoid standard deactivation flow when we need to show propagate formula dialog
    // We have to do this, because formula dialog is defined in another package
    // and loaded dynamically by @angular/router.
    // When we are in leaving current route and start another route transition (and wait when it completes)
    // we block navigation at all!!!
    // This way we reject current deactivation, start leave flow manually and navigate when it completes
    if (!this.form.dirty) {
      return true;
    } else if (!needsFormulaPropagation(this.form.submitValue)) {
      return super.canDeactivate();
    }

    wrapInObservable(super.canDeactivate()).pipe(
      filter$(Boolean))
      .subscribe(() => {
        this.form.markAsPristine();
        this.router.navigateByUrl(nextState!.url); // tslint:disable-line:no-non-null-assertion
      });

    return false;
  }

  getUomName(uomId?: ID): string {
    return uomId ? <string>get(find(this.uoms, { id: uomId }), 'name', '') : '';
  }

  onRecalculate(): void {
    const flow = this.pipelines.recalculate({
      validateForm: this.validateFormAction,
      validate: this.validateAction,
      execute: this.commonActions.execute((state: FormFlowState<ComponentDetails>) =>
        this.businessService.recalculate(state.value)),
      finish: (state: FormFlowState<ComponentDetails>) => {
        const { value, form } = state;
        (<FormState<ComponentDetails>>form).build(value, true);
      },
    });

    flow.run({
      form: this.form,
      value: this.form.submitValue,
    });
  }

  goToAel(): void {
    this.navigationService.goToAel({
      entityId: this.form.id,
      entityType: 'COMPONENT',
      label: this.title,
    });
  }

  onFormulaChange(): void {
    this.setFormulaNeedPropagation(true);
  }

  private showError(state: FormFlowState<ComponentDetails>): void {
    if (whenFormInvalid(state)) {
      highlightAll(state);
    } else if (whenNoItems(state.value)) {
      this.notificationService.error('msg_component_no_items_selected');
    } else {
      this.selectedTab = ComponentDetailsTab.Items;
      this.commands.navigateToInvalid();
    }
  }

  private handleErrors(state: FormFlowState<ComponentDetails>): void {
    highlightAll(state);

    this.validateFormula();
  }

  private validateFormula(): boolean {
    if (this.form.isFormulaInvalid()) {
      this.selectedTab = ComponentDetailsTab.Formula;
      this.formulaBuilder.test();
      return false;
    }
    return true;
  }

  private propagateFormula(): Observable<void> {
    const { formulaContext } = getRouteInitData(this.route);
    return from$(this.formulaPropagation.propagateFormula({
      formulaContext,
      elementId: getControlValue(this.form.group, 'id'),
      elementCode: getControlValue(this.form.group, 'code'),
      isComponent: true,
      newFormula: getControlValue(this.form.group, 'formula'),
      oldFormula: getControlValue(this.form.group, 'oldFormula'),
    }).then(() => this.setFormulaNeedPropagation(false)));
  }

  private setFormulaNeedPropagation(needPropagation: boolean): void {
    setControlValue(this.form.group, 'isFormulaNeedPropagation', needPropagation);
  }
}
